import { Component, Input, OnInit } from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { UserDao } from '@app/dao/user/user.dao';
import { combineLatest } from 'rxjs';

/**
 * Détenteur avec sa part de détention.
 * `share` est une fraction 0..1 (ex. 0.5 = 50 %), alignée sur l'API
 * (`LoanOwnerDto` / `InvestmentOwnerDto`).
 */
export interface OwnerShare {
  ownerId: number;
  share: number;
}

/**
 * Construit la valeur initiale du champ `owners` (`OwnerShare[]`) à partir des
 * détenteurs d'une entité.
 * - share existant : on le reprend tel quel ;
 * - share absent (legacy `ownerIds` / `{ ownerId }`) : répartition égale 1/n,
 *   le dernier détenteur absorbant l'arrondi ;
 * - aucun détenteur : tableau vide.
 */
export function buildInitialOwnerShares(entity: any): OwnerShare[] {
  const owners = entity?.owners;
  if (!Array.isArray(owners) || !owners.length) return [];
  const hasShare = owners.some(
    o => o && typeof o === 'object' && o.share != null,
  );
  const n = owners.length;
  const each = Math.round((1 / n) * 100) / 100;
  return owners.map((o, i) => {
    const ownerId = o && typeof o === 'object' ? o.ownerId : o;
    let share: number;
    if (hasShare) {
      share = o && typeof o === 'object' && o.share != null ? o.share : 0;
    } else {
      share = i === n - 1 ? Math.round((1 - each * (n - 1)) * 100) / 100 : each;
    }
    return { ownerId: Number(ownerId), share };
  });
}

/**
 * Sélection des détenteurs avec part de détention (`share`).
 *
 * Remplace `pr-input-owner` : on ajoute des détenteurs (sans l'option COUPLE
 * « En commun » : on sélectionne M. et Mme séparément) et on saisit le
 * pourcentage de détention de chacun. Répartition par défaut à parts égales
 * (1/n) ; la somme des parts ne peut pas dépasser 100 %.
 *
 * Valeur du contrôle (ControlValueAccessor) : `OwnerShare[]`.
 */
@Component({
  selector: 'pr-input-owner-share',
  templateUrl: './pr-input-owner-share.component.html',
  styleUrls: ['./pr-input-owner-share.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: PrInputOwnerShareComponent,
    },
  ],
  standalone: false,
})
export class PrInputOwnerShareComponent
  implements ControlValueAccessor, OnInit
{
  @Input() formControlName: string;
  @Input() derrors: boolean = false;
  @Input() required: boolean = false;
  @Input() label: string = 'Propriétaire(s)';
  @Input() tooltipButton: string;

  options: Array<{ id: number; label: string }> = [];
  values: OwnerShare[] = [];
  touched = false;
  disabled = false;
  onChange = (_values: OwnerShare[]) => {};
  onTouched = () => {};

  constructor(
    private userDao: UserDao,
    private controlContainer: ControlContainer,
  ) {}

  get control() {
    return this.controlContainer.control.get(this.formControlName);
  }

  /** Détenteurs non encore sélectionnés (proposés à l'ajout). */
  availableOptions: Array<{ id: number; label: string }> = [];

  /**
   * Options au format attendu par `pr-select-form` : `{ id, value }`
   * (`id` = valeur sélectionnée = ownerId ; `value` = libellé affiché).
   * Référence stable (recalculée seulement quand la sélection ou la liste des
   * détenteurs change) : `pr-select-form` traque ses options par référence
   * (`track item`), un nouveau tableau à chaque CD provoquerait un recalcul en boucle.
   */
  addSelectOptions: Array<{ id: number; value: string }> = [];

  private refreshOptions() {
    const taken = new Set(this.values.map(v => v.ownerId));
    this.availableOptions = this.options.filter(o => !taken.has(o.id));
    this.addSelectOptions = this.availableOptions.map(o => ({
      id: o.id,
      value: o.label,
    }));
  }

  get totalPct(): number {
    return Math.round(
      this.values.reduce((sum, o) => sum + (o.share || 0), 0) * 100,
    );
  }

  get isOver(): boolean {
    return this.totalPct > 100;
  }

  ngOnInit() {
    // Liste des détenteurs sélectionnables (conjoints d'abord, puis les autres),
    // SANS l'option COUPLE « En commun ».
    combineLatest([
      this.userDao.getCouple(),
      this.userDao.getOwners(),
    ]).subscribe(([couple, owners]) => {
      const seen = new Set<number>();
      const opts: Array<{ id: number; label: string }> = [];
      for (const user of couple) {
        const owner = owners.find(o => o.id == user.id);
        if (owner && !seen.has(owner.id)) {
          seen.add(owner.id);
          opts.push({ id: owner.id, label: owner.label });
        }
      }
      for (const owner of owners) {
        if (!seen.has(owner.id)) {
          seen.add(owner.id);
          opts.push({ id: owner.id, label: owner.label });
        }
      }
      this.options = opts;
      this.refreshOptions();
    });
  }

  labelFor(ownerId: number): string {
    const o = this.options.find(x => x.id === ownerId);
    return o ? o.label : `Détenteur ${ownerId}`;
  }

  toDisplayPct(share: number): number {
    return Math.round((share || 0) * 100);
  }

  private round2(x: number): number {
    return Math.round(x * 100) / 100;
  }

  /** Répartit la détention à parts égales (1/n), le dernier absorbe l'arrondi. */
  private redistribute() {
    const n = this.values.length;
    if (!n) return;
    const each = this.round2(1 / n);
    let acc = 0;
    this.values = this.values.map((o, i) => {
      const share = i === n - 1 ? this.round2(1 - acc) : each;
      acc = this.round2(acc + share);
      return { ...o, share };
    });
  }

  onAdd(ownerId: number) {
    // Le sélecteur d'ajout est remis à null directement dans le template
    // (`addSelect.value = null`) : il ne conserve donc jamais de valeur, ce qui
    // évite qu'un détenteur retiré réapparaisse dans le sélecteur.
    if (ownerId == null || this.values.some(v => v.ownerId === ownerId)) {
      return;
    }
    this.values = [...this.values, { ownerId: Number(ownerId), share: 0 }];
    this.redistribute();
    this.refreshOptions();
    this.emit();
  }

  onRemove(ownerId: number) {
    this.values = this.values.filter(v => v.ownerId !== ownerId);
    this.redistribute();
    this.refreshOptions();
    this.emit();
  }

  onShareChange(ownerId: number, displayPct: number) {
    const owner = this.values.find(o => o.ownerId === ownerId);
    if (!owner) return;
    const pct = isNaN(displayPct) ? 0 : Math.max(0, displayPct);
    owner.share = this.round2(pct / 100);
    this.emit();
  }

  redistributeEqually() {
    this.redistribute();
    this.emit();
  }

  private emit() {
    this.markAsTouched();
    this.onChange([...this.values]);
  }

  // ─── ControlValueAccessor ───────────────────────────────────────────────
  writeValue(values: OwnerShare[]): void {
    this.values = Array.isArray(values)
      ? values.map(o => ({
          ownerId: Number(o.ownerId),
          share: o.share != null ? Number(o.share) : 0,
        }))
      : [];
    this.refreshOptions();
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
